import { nanoid } from "nanoid";

export type ThreadState =
  | { status: "draft"; draftChatId: string }
  | { status: "persisting"; draftChatId: string }
  | { status: "persisted"; persistedChatId: string; draftChatId?: string };

export type ThreadAction =
  | { type: "SYNC_PROP"; chatId: string | null }
  | { type: "START_CREATE" }
  | { type: "CREATED"; newId: string }
  | { type: "ERROR" };

const createDraftThreadState = (draftChatId = nanoid()): ThreadState => ({
  status: "draft",
  draftChatId,
});

export const createInitialThreadState = (
  activeChatId?: string | null,
): ThreadState => {
  if (activeChatId) {
    return {
      status: "persisted",
      persistedChatId: activeChatId,
      draftChatId: undefined,
    };
  }
  return createDraftThreadState();
};

export function threadReducer(
  state: ThreadState,
  action: ThreadAction,
): ThreadState {
  switch (action.type) {
    case "SYNC_PROP": {
      const { chatId } = action;
      // Case 1: Switching to "New Chat" (null)
      if (!chatId) {
        return createDraftThreadState();
      }

      // Case 2: Navigating to existing chat (or same chat)
      if (state.status === "persisted" && state.persistedChatId === chatId) {
        return state; // No change
      }

      // Switching to a different existing chat
      return {
        status: "persisted",
        persistedChatId: chatId,
        draftChatId: undefined, // Clear any legacy draft ID
      };
    }
    case "START_CREATE":
      // Valid only from draft?
      if (state.status !== "draft") return state;
      return { status: "persisting", draftChatId: state.draftChatId };

    case "CREATED":
      if (state.status !== "persisting") return state;
      return {
        status: "persisted",
        persistedChatId: action.newId,
        // CRITICAL: We KEEP the current draftChatId so useChat does not reset.
        draftChatId: state.draftChatId,
      };

    case "ERROR":
      // Revert from persisting to active/draft based on previous?
      // If we failed to create, we are back to draft.
      if (state.status === "persisting") {
        return { status: "draft", draftChatId: state.draftChatId };
      }
      return state;

    default:
      return state;
  }
}
